import { useEffect, useState } from "react";
import { GSButton, GSInfoGroup } from "golfstatus_react_components";
import {
  faArrowRight,
  faCircleCheck,
  faListCheck,
  faPen,
  faPlus,
  faSpinner,
  faTrash,
  faUsers,
  faUsersSlash,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RECIPIENT_GROUP_LABELS } from "../../../forms/MessageForms";
import "./tournament-message-recipient-group-line-item.scss";

const TournamentMessageRecipientGroupLineItem = (props) => {
  const {
    groupType,
    recipientCount,
    sentCount,
    includeAll,
    onManage,
    onAdd,
    onRemove,
    onView,
    selectionView,
    isLocked,
    hasRecipients,
    isEmptyState,
    isExcluded,
    messageType = "message",
    isAddLoading,
    onSelectRecipients,
    showDisclaimer = true,
  } = props;

  const [wasAddClicked, setWasAddClicked] = useState(false);
  useEffect(() => {
    if (!isAddLoading) setWasAddClicked(false);
  }, [isAddLoading]);

  if (isEmptyState) {
    const emptyDataGroups = [
      {
        sections: [
          {
            sectionItems: [
              {
                type: "primary",
                value: (
                  <FontAwesomeIcon
                    className="recipient-icon"
                    icon={faUserPlus}
                  />
                ),
              },
            ],
          },
        ],
      },
      {
        fit: "stretch",
        sections: [
          {
            sectionItems: [
              {
                type: "secondary full-width",
                value: "No Recipients Selected",
                sectionItemStyle: { whiteSpace: "normal" },
              },
            ],
          },
        ],
      },
      ...(isLocked
        ? []
        : [
            {
              sections: [
                {
                  sectionItems: [
                    {
                      type: "primary no-wrap",
                      value: (
                        <GSButton
                          buttonIcon={faPen}
                          type="light-grey"
                          onClick={onSelectRecipients}
                        />
                      ),
                    },
                  ],
                },
              ],
            },
          ]),
    ];
    return (
      <tournament-message-recipient-group-line-item>
        <div className="line-item-container">
          <GSInfoGroup dataGroups={emptyDataGroups} />
        </div>
      </tournament-message-recipient-group-line-item>
    );
  }

  const label = isExcluded
    ? "Registered Participants Excluded"
    : RECIPIENT_GROUP_LABELS[groupType] ?? groupType;
  const selected = recipientCount ?? 0;
  const isAdded = includeAll === true || selected > 0;

  const showAddSpinner = wasAddClicked && isAddLoading;

  const handleAdd = () => {
    setWasAddClicked(true);
    onAdd?.();
  };

  const getSubtitle = () => {
    if (isExcluded) {
      if (isLocked) return null;
      return `Participants will not receive this ${messageType}, even if included in other recipient groups.`;
    }
    if (isLocked && sentCount != null) {
      return `Sent to ${sentCount} Recipient${sentCount === 1 ? "" : "s"}`;
    }
    if (includeAll === true) return "Sending to All Available Recipients";
    if (selected > 0) return `${selected} Selected Recipient${selected === 1 ? "" : "s"}`;
    return null;
  };

  const subtitle = getSubtitle();

  const getIcon = () => {
    if (isExcluded) return faUsersSlash;
    if (isAdded) return faCircleCheck;
    return faUsers;
  };

  const infoSection = {
    sectionItems: [
      {
        type: "primary full-width",
        value: (
          <div className="group-label">
            <FontAwesomeIcon
              className={`recipient-icon${isAdded && !isExcluded ? " is-added" : ""}`}
              icon={getIcon()}
            />
            <div className="group-label-text">
              <div className="group-label-title">{label}</div>
              {subtitle && (
                <div className="group-label-subtitle">{subtitle}</div>
              )}
            </div>
          </div>
        ),
        sectionItemStyle: { whiteSpace: "normal" },
      },
    ],
  };

  const getActionSection = () => {
    if (isExcluded) return null;
    if (!selectionView) {
      if (!onView) return null;
      return {
        sectionItems: [
          {
            type: "primary no-wrap",
            value: (
              <GSButton
                buttonIcon={faArrowRight}
                type="white no-wrap"
                onClick={onView}
              />
            ),
          },
        ],
      };
    }

    if (isAdded) {
      return {
        sectionItems: [
          {
            type: "primary no-wrap",
            value: (
              <div className="action-row">
                <GSButton
                  title="Manage"
                  buttonIcon={faListCheck}
                  type="light-grey no-wrap mobile-icon"
                  onClick={onManage}
                />
                <GSButton
                  buttonIcon={faTrash}
                  type="white"
                  onClick={onRemove}
                />
              </div>
            ),
          },
        ],
      };
    }

    return {
      sectionItems: [
        {
          type: "primary no-wrap",
          value: (
            <GSButton
              title="Add Group"
              buttonIcon={showAddSpinner ? faSpinner : faPlus}
              type="white no-wrap mobile-icon"
              onClick={handleAdd}
              isDisabled={!hasRecipients || showAddSpinner}
            />
          ),
        },
      ],
    };
  };

  const dataGroups = [
    { fit: "stretch", sections: [infoSection] },
  ];

  const actionSection = getActionSection();
  if (actionSection) {
    dataGroups.push({ sections: [actionSection] });
  }

  const showGroupDisclaimer = includeAll === true && !isLocked && showDisclaimer;

  const getDisclaimerText = () => {
    switch (groupType) {
      case "sponsors":
        return "Removal or edits to sponsor contact information may affect who receives this message";
      case "players":
        return "Removal, disqualification, or edits to player contact information may affect who receives this message";
      case "waitlist":
        return "Changes to the waitlist contact information may affect who receives this message";
      case "customers":
        return "Refunds, cancellations or edits to customer information may affect who receives this message";
      case "donors":
        return "Refunds, cancellations or edits to donor information may affect who receives this message";
      default:
        return "Changes to this group may affect who receives this message";
    }
  };

  return (
    <tournament-message-recipient-group-line-item>
      <div className={`line-item-container ${isAdded || isExcluded ? "is-added" : "is-not-added"}`}>
        <GSInfoGroup dataGroups={dataGroups} />
        {showGroupDisclaimer && (
          <div className="group-disclaimer">{getDisclaimerText()}</div>
        )}
      </div>
    </tournament-message-recipient-group-line-item>
  );
};

export default TournamentMessageRecipientGroupLineItem;
